import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';

/** Table skeleton – mimics a data table with header + rows */
export const TableSkeleton = ({ rows = 5, cols = 4 }) => (
  <div className="border rounded-lg overflow-hidden" data-testid="table-skeleton">
    {/* Header */}
    <div className="bg-slate-50 border-b px-4 py-3 flex gap-4">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className="h-3.5 rounded" style={{ width: `${[25, 20, 18, 15, 12][i % 5]}%` }} />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="px-4 py-3 flex gap-4 border-b last:border-b-0">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className="h-3 rounded" style={{ width: `${[22, 18, 20, 14, 10][(r + c) % 5]}%` }} />
        ))}
      </div>
    ))}
  </div>
);

/** Card skeleton – mimics a collapsible supplier/group card */
export const CardSkeleton = ({ count = 3 }) => (
  <div className="space-y-3" data-testid="card-skeleton">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="border rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-4 w-28 rounded" />
            <Skeleton className="h-5 w-14 rounded-full" />
            <Skeleton className="h-5 w-14 rounded-full" />
          </div>
          <Skeleton className="h-4 w-4 rounded" />
        </div>
        <div className="px-4 py-3 space-y-2">
          {Array.from({ length: 2 }).map((_, j) => (
            <Skeleton key={j} className="h-3 rounded" style={{ width: `${65 + j * 12}%` }} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/** Dashboard skeleton – stats cards + shortcut grid */
export const DashboardSkeleton = () => (
  <div className="space-y-6" data-testid="dashboard-skeleton">
    {/* Title area */}
    <div>
      <Skeleton className="h-8 w-40 rounded mb-2" />
      <Skeleton className="h-4 w-56 rounded" />
    </div>
    {/* Alert cards */}
    <div className="space-y-3">
      <div className="border rounded-lg p-4 flex items-start gap-3">
        <Skeleton className="h-5 w-5 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/5 rounded" />
          <Skeleton className="h-3 w-4/5 rounded" />
          <Skeleton className="h-8 w-24 rounded mt-1" />
        </div>
      </div>
    </div>
    {/* Shortcut grid */}
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="border rounded-lg p-4 space-y-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-3/4 rounded" />
          <Skeleton className="h-3 w-full rounded" />
        </div>
      ))}
    </div>
  </div>
);

/** List skeleton – for order/item lists */
export const ListSkeleton = ({ rows = 6 }) => (
  <div className="space-y-2" data-testid="list-skeleton">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="border rounded-lg px-4 py-3 flex items-center gap-3">
        <Skeleton className="h-4 w-4 rounded shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 rounded" style={{ width: `${55 + (i * 7) % 30}%` }} />
          <Skeleton className="h-3 rounded" style={{ width: `${35 + (i * 11) % 25}%` }} />
        </div>
        <Skeleton className="h-6 w-16 rounded" />
      </div>
    ))}
  </div>
);

/** Stats row skeleton – horizontal stat badges */
export const StatsSkeleton = ({ count = 4 }) => (
  <div className="flex gap-3 flex-wrap" data-testid="stats-skeleton">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="border rounded-lg px-4 py-3 flex-1 min-w-[120px] space-y-2">
        <Skeleton className="h-3 w-16 rounded" />
        <Skeleton className="h-6 w-12 rounded" />
      </div>
    ))}
  </div>
);

/** Generic page loading skeleton – title + content area */
export const PageSkeleton = ({ children }) => (
  <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5" data-testid="page-skeleton">
    <div>
      <Skeleton className="h-7 w-48 rounded mb-2" />
      <Skeleton className="h-4 w-72 rounded" />
    </div>
    {children || <TableSkeleton />}
  </div>
);
